import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Tooltip } from '@mui/material';
import { useAuth } from '../context/AuthContext';

export default function EventParticipantsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();

  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchMatrix = async () => {
    if (!user || role !== 'admin') return;
    setLoading(true);
    setError('');
    try {
      const token = await user.getIdToken();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
  const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/admin/matrix`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.message || json?.meta?.reason || `HTTP ${resp.status}`);
      setMatrix(json);
    } catch (e) {
      setError(e.message || 'Failed to load matches');
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMatrix(); /* eslint-disable-next-line */ }, [eventId, role, user]);

  const exportCsv = () => {
    if (!matrix) return;
    const headers = ['User', 'Ticket Type', 'Status', 'Likes', 'Liked By', 'Mutual (Active)', 'Severed With'];
    const rows = (matrix.users || []).map(row => {
      const idx = matrix.userIndex || {};
      const me = idx[row.uid] || { name: row.uid, status: '', ticketType: '' };
      const likes = (row.likes || []).map(u => idx[u]?.name || u).join('; ');
      const likedBy = (row.likedBy || []).map(u => idx[u]?.name || u).join('; ');
      const mutual = (row.mutual || []).map(u => idx[u]?.name || u).join('; ');
      const severed = (row.severedWith || []).map(u => idx[u]?.name || u).join('; ');
      return [me.name, me.ticketType || '', me.status || '', likes, likedBy, mutual, severed];
    });
    const meta = matrix.meta || {};
    const activeMutual = meta.mutualPairsCountActive ?? meta.mutualPairsCount ?? '';
    const allMutual = meta.mutualPairsCountAll ?? '';
    const severedPairs = meta.severedPairsCount ?? '';
    const preface = `Participants: ${meta.participantsTotal ?? meta.participants ?? ''}${typeof meta.participantsActive === 'number' ? ` (Active: ${meta.participantsActive})` : ''}, Forms: ${meta.formsSubmitted ?? meta.matchesDocs ?? ''}${typeof meta.selectionsTotal === 'number' ? `, Selections: ${meta.selectionsTotal}` : ''}, Mutual Pairs (Active): ${activeMutual}${allMutual !== '' ? `, Mutual Pairs (All): ${allMutual}` : ''}${severedPairs !== '' ? `, Severed Pairs: ${severedPairs}` : ''}`;
    const csvLines = [];
    csvLines.push(`# Event ${eventId} – Matches Summary`);
    csvLines.push(`# ${preface}`);
    csvLines.push(headers.join(','));
    rows.forEach(r => {
      const escaped = r.map(field => {
        const val = String(field ?? '');
        if (/[",\n]/.test(val)) return '"' + val.replace(/"/g, '""') + '"';
        return val;
      });
      csvLines.push(escaped.join(','));
    });
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event_${eventId}_matches.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (role !== 'admin') return <Box sx={{ p: 2 }}><Alert severity="error">Admins only.</Alert></Box>;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: { xs: 2, sm: 4 }, p: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 22, sm: 28 } }}>Participants & Matches</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={fetchMatrix} disabled={loading}>Refresh</Button>
          <Button size="small" variant="contained" onClick={exportCsv} disabled={!matrix || loading}>Export CSV</Button>
        </Box>
      </Box>
      {loading && <Typography>Loading…</Typography>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !error && matrix && (
        <Box sx={{ mt: 1 }}>
          {(() => {
            const m = matrix.meta || {};
            const parts = m.participantsTotal ?? m.participants ?? 0;
            const active = m.participantsActive ?? undefined;
            const forms = m.formsSubmitted ?? m.matchesDocs ?? 0;
            const selections = m.selectionsTotal ?? undefined;
            const mutualActive = m.mutualPairsCountActive ?? m.mutualPairsCount ?? 0;
            const mutualAll = m.mutualPairsCountAll ?? undefined;
            const severedPairs = m.severedPairsCount ?? undefined;
            return (
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Participants: {parts}{typeof active === 'number' ? ` (Active: ${active})` : ''} · Forms: {forms}{typeof selections === 'number' ? ` · Selections: ${selections}` : ''} · Mutual Pairs (Active): {mutualActive}{typeof mutualAll === 'number' ? ` · Mutual Pairs (All): ${mutualAll}` : ''}{typeof severedPairs === 'number' ? ` · Severed Pairs: ${severedPairs}` : ''}
              </Typography>
            );
          })()}
          {(matrix.users || []).length === 0 && <Typography>No match selections submitted yet.</Typography>}
          {(matrix.users || []).map(row => {
            const idx = matrix.userIndex || {};
            const me = idx[row.uid] || { name: row.uid };
            const likeNames = (row.likes || []).map(uid => (idx[uid]?.name || uid));
            const likedByNames = (row.likedBy || []).map(uid => (idx[uid]?.name || uid));
            const mutualNames = (row.mutual || []).map(uid => (idx[uid]?.name || uid));
            const severedNames = (row.severedWith || []).map(uid => (idx[uid]?.name || uid));
            const sevIndex = (matrix.meta && matrix.meta.severedIndex) || {};
            return (
              <Box key={row.uid} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1, bgcolor: '#fafafa', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <span>{me.name}</span>
                  {me.ticketType && (
                    <span style={{ fontSize: 11, border: '1px solid #ddd', borderRadius: 4, padding: '1px 6px', background: '#fff' }}>
                      {String(me.ticketType).toLowerCase() === 'premium' ? 'Premium' : 'Basic'}
                    </span>
                  )}
                  {me.status && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, px: 0.5, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fff' }}>
                      {me.status}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.25 }}>
                  Likes → {likeNames.length ? likeNames.join(', ') : '—'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.25 }}>
                  Liked By ← {likedByNames.length ? likedByNames.join(', ') : '—'}
                </Typography>
                <Typography variant="body2" sx={{ color: mutualNames.length ? '#2e7d32' : '#777', mb: 0.25 }}>
                  Mutual (Active) ✓ {mutualNames.length ? mutualNames.join(', ') : '—'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'baseline' }}>
                  <Typography variant="body2" sx={{ color: severedNames.length ? '#b26a00' : '#777' }}>
                    Severed ✕
                  </Typography>
                  {severedNames.length ? (
                    severedNames.map(name => {
                       // Find uid by name fallback; if duplicate names occur, tooltip may be ambiguous but still useful
                       const uid = Object.keys(idx).find(k => (idx[k]?.name === name));
                       const key = [row.uid, uid].filter(Boolean).map(String).sort().join('::');
                       const meta = sevIndex[key] || {};
                       const when = meta.severedAt ? new Date(meta.severedAt).toLocaleString() : '—';
                       const by = meta.severedByName || meta.severedBy || 'unknown';
                       const tip = `By: ${by}\nReason: ${meta.reason || '—'}\nWhen: ${when}`;
                       return (
                        <Tooltip key={name+key} title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ textDecoration: 'underline dotted', cursor: 'help', mr: 0.75, fontFamily: (theme) => theme.typography.fontFamily }}
                          >
                            {name}
                          </Typography>
                        </Tooltip>
                       );
                     })
                   ) : (
                     <Typography variant="body2" sx={{ color: '#777' }}>—</Typography>
                   )}
                 </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
